import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { DateRangePicker } from '@modules/common/components/DateRangePicker';
import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { getAggregatePeriodFromDateRange } from '@modules/common/utils';
import { EditableCompareFieldName } from '@modules/quantify/components/QuantifyQueryBuilder/Fields/EditableCompareFieldName/EditableCompareFieldName';
import { DEFAULT_COMPARE_DURATION_A_NAME } from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useGetFormattedDurationFieldLabels,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getCompareToPreviousPeriodFromDuration,
  getDefaultCompareToCustomAbsoluteDuration,
  isValidQuery,
  trackQuantifyCompareEvents,
} from '@modules/quantify/utils';
import { Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

export const CompareDurationA = () => {
  const {
    tenant: { timezone },
  } = useGetUser();

  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const duration = useQuantifyStore(({ query }) => query.duration);
  const compareTo = useQuantifyStore(({ query }) => query.compareQuery);
  const isValidQueryState = useQuantifyStore(({ query }) =>
    isValidQuery(query),
  );
  const isTableQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isTableQueryInProgress,
  );
  const isChartQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isChartQueryInProgress,
  );

  const compareDurationAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_DURATION_A_NAME,
  );

  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const analysisLink = useAnalysisStore(({ href }) => href);

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
    }
  }, [fetchReportData, isValidQueryState, shouldFetchReportData]);

  const onDurationChange = (
    newDuration: DateTimeRange,
    isQuickLookupOption?: boolean,
  ) => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        duration: newDuration,
        shouldShowTopDataPoints: true,
        visibleDataPoints: [],
      },
    });

    if (isQuickLookupOption) {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          aggregatePeriod: getAggregatePeriodFromDateRange(newDuration),
        },
      });
    }

    if (!!compareTo?.previousPeriod) {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          compareQuery:
            newDuration.type === 'last'
              ? getCompareToPreviousPeriodFromDuration(newDuration)
              : getDefaultCompareToCustomAbsoluteDuration(timezone),
        },
      });
    }

    if (
      !!compareTo?.timerange &&
      !compareTo.timerange.relative &&
      newDuration.type !== 'last'
    ) {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          compareQuery: getDefaultCompareToCustomAbsoluteDuration(timezone),
        },
      });
    }

    setShouldFetchReportData(true);
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - Duration A Changed',
      link: analysisLink,
    });
  };

  const onChangeDurationAName = (fieldAName: string) => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        compareMetadata: {
          baseQueryName: fieldAName,
        },
      },
    });
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - Duration A renamed',
      filterName: fieldAName,
      link: analysisLink,
    });
  };

  const quickOptionsFilters = useMemo(() => ({ skipYesterday: true }), []);

  const { durationLabel } = useGetFormattedDurationFieldLabels({
    shouldShowForAllDurationTypes: true,
  });

  return (
    <Stack spacing={1} pb={2} data-testid="quantify-compare-duration-field-a">
      <Stack
        direction="row"
        alignItems="center"
        columnGap={1}
        rowGap={1}
        flexWrap="wrap"
      >
        <EditableCompareFieldName
          initialFieldName={compareDurationAName}
          onChange={onChangeDurationAName}
        />
        {durationLabel.secondary && (
          <Typography variant="caption" color="text.secondary">
            ({durationLabel.secondary})
          </Typography>
        )}
      </Stack>
      <DateRangePicker
        isReadOnly={isTableQueryInProgress || isChartQueryInProgress}
        timeRange={duration}
        onChange={onDurationChange}
        shouldShowQuickLookupOptions
        quickOptionsFilters={quickOptionsFilters}
      />
    </Stack>
  );
};
